import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
    useGetEmployeeByIdQuery,
    useResendInviteMutation,
} from "../../../common/api/employee";
import { useAppSelector } from "../../../app/hooks";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { getFunctionGroupLabel } from "../../../common/utils/getFunctionGroupLabel";
import { getUserFullName } from "../../../common/utils/getUserFullName";

type UseEmployeeDetailHeaderResult = {
    isLoading: boolean;
    employeeId: number;
    hasEmployeeData: boolean;
    name: string;
    statusName: string;
    statusColorCode: string;
    establishmentName: string;
    hasFunctionGroups: boolean;
    functionGroupLabel?: string;
    canResend: boolean;
    isResending: boolean;
    onResend: () => Promise<void>;
};

export const useEmployeeDetailHeader = (): UseEmployeeDetailHeaderResult => {
    const employeeId = Number(useParams().employeeId);
    const establishmentId = useEstablishmentId();
    const [statusFetching, setStatusFetching] = useState(false);

    const { isPostCreateEmployeeSuccess } = useAppSelector(
        (state) => state.createEmployeeModal,
    );

    const {
        data: employeeData,
        isLoading: employeeDataIsLoading,
        refetch,
    } = useGetEmployeeByIdQuery({ id: employeeId });

    const [resendInvite] = useResendInviteMutation();

    const onResend = async (): Promise<void> => {
        if (!employeeId || !establishmentId) return;
        setStatusFetching(true);
        try {
            await resendInvite({
                id: employeeId,
                data: { establishment_id: establishmentId },
            }).unwrap();
            await refetch();
            toast.success("Medewerker is opnieuw uitgenodigd");
        } catch (error) {
            console.error("Failed to activate/deactivate employee:", error);
            toast.error(
                "Er is iets misgegaan bij het opnieuw uitnodigen van de medewerker",
            );
        }
        setStatusFetching(false);
    };

    useEffect(() => {
        if (isPostCreateEmployeeSuccess) {
            refetch();
        }
    }, [isPostCreateEmployeeSuccess, refetch]);

    const {
        name,
        statusName,
        statusColorCode,
        establishmentName,
        hasFunctionGroups,
        functionGroupLabel,
        canResend,
    } = useMemo(() => {
        const fullName = employeeData
            ? getUserFullName(employeeData.user) || ""
            : "";
        const statusNameLocal = employeeData?.status?.name ?? "";
        const statusColorCodeLocal = employeeData?.status?.color_code ?? "";
        const establishmentNameLocal = employeeData?.establishment?.name ?? "";
        const hasFunctionGroupsLocal =
            (employeeData?.function_groups?.length ?? 0) > 0;
        const functionGroupLabelLocal = hasFunctionGroupsLocal
            ? getFunctionGroupLabel(employeeData!.function_groups)
            : undefined;
        const canResendLocal =
            employeeData?.status?.code === "invite_expired" && !statusFetching;
        return {
            name: fullName,
            statusName: statusNameLocal,
            statusColorCode: statusColorCodeLocal,
            establishmentName: establishmentNameLocal,
            hasFunctionGroups: hasFunctionGroupsLocal,
            functionGroupLabel: functionGroupLabelLocal,
            canResend: Boolean(canResendLocal),
        };
    }, [employeeData, statusFetching]);

    return {
        isLoading: employeeDataIsLoading,
        employeeId,
        hasEmployeeData: Boolean(employeeData),
        name,
        statusName,
        statusColorCode,
        establishmentName,
        hasFunctionGroups,
        functionGroupLabel,
        canResend,
        isResending: statusFetching,
        onResend,
    };
};
